import { motion } from "framer-motion";
import { Trophy } from "lucide-react";
import { useEffect, useState } from "react";

import { audioService } from "@/core/audio/audioService";
import Confetti from "./effects/Confetti";
import Fireworks from "./effects/Fireworks";

const STAGE_LABEL = {
  one_line: "1 LINE WINNER",
  two_lines: "2 LINES WINNER",
  full_house: "FULL HOUSE WINNER",
};

/**
 * WinnerOverlayBasic — cinematic winner celebration (Step 9).
 *
 * Sequence:
 *   0.00s  Backdrop fade-in + celebration sound + confetti burst
 *   0.15s  Fireworks bursts (staggered every 0.5s)
 *   0.20s  Stage tag drops in ("FULL HOUSE WINNER")
 *   0.55s  Trophy pops with glow pulse
 *   0.90s  Giant "WINNER!" title scales in with radial glow
 *   1.60s  Winner name reveals character-by-character (typewriter)
 *
 * Overlay is dismissed by the Host clicking "Dismiss Overlay".
 */
export default function WinnerOverlayBasic({ stage, name }) {
  const [typed, setTyped] = useState("");
  const displayName = (name || "").trim();

  // Play the celebration sound once on mount.
  useEffect(() => {
    audioService.playWinner();
  }, []);

  // Typewriter reveal — kicks in at ~1.6s to sit under the WINNER title.
  useEffect(() => {
    if (!displayName) return;
    setTyped("");
    const startDelay = 1600;
    const perChar = 70;
    const timers = [];
    const start = window.setTimeout(() => {
      for (let i = 1; i <= displayName.length; i++) {
        timers.push(
          window.setTimeout(
            () => setTyped(displayName.slice(0, i)),
            (i - 1) * perChar,
          ),
        );
      }
    }, startDelay);
    return () => {
      window.clearTimeout(start);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [displayName]);

  return (
    <motion.div
      data-testid="projector-winner-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-20 flex items-center justify-center px-16 overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 50% 45%, rgba(251,191,36,0.18) 0%, rgba(0,0,0,0.72) 55%, rgba(0,0,0,0.88) 100%)",
        backdropFilter: "blur(14px)",
      }}
    >
      {/* Confetti + Fireworks layered behind the text */}
      <Confetti count={120} />
      <Fireworks burstCount={4} />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 22 }}
        className="ic-font-display flex flex-col items-center text-center relative z-10"
      >
        {/* Stage tag */}
        <motion.div
          initial={{ y: -18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-xs tracking-[0.6em] uppercase mb-6"
          style={{ color: "var(--ic-primary)" }}
          data-testid="projector-winner-stage-tag"
        >
          {STAGE_LABEL[stage] || "WINNER"}
        </motion.div>

        {/* Trophy */}
        <motion.div
          initial={{ scale: 0, rotate: -30, opacity: 0 }}
          animate={{
            scale: [0, 1.2, 1],
            rotate: [-30, 8, 0],
            opacity: 1,
          }}
          transition={{ delay: 0.55, duration: 0.7, ease: "easeOut" }}
          className="mb-6"
        >
          <motion.div
            animate={{
              filter: [
                "drop-shadow(0 0 20px var(--ic-primary-glow))",
                "drop-shadow(0 0 50px var(--ic-primary-glow))",
                "drop-shadow(0 0 20px var(--ic-primary-glow))",
              ],
            }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Trophy
              style={{
                width: "clamp(4rem, 6vw, 8rem)",
                height: "clamp(4rem, 6vw, 8rem)",
                color: "var(--ic-primary)",
              }}
              strokeWidth={2}
            />
          </motion.div>
        </motion.div>

        {/* Big cinematic WINNER! title */}
        <motion.div
          initial={{ scale: 0.6, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{
            delay: 0.9,
            duration: 0.65,
            type: "spring",
            stiffness: 180,
            damping: 18,
          }}
          className="relative"
        >
          <motion.h1
            data-testid="projector-winner-title"
            className="font-black leading-none"
            style={{
              fontSize: "clamp(5rem, 14vw, 16rem)",
              color: "var(--ic-text-primary)",
              letterSpacing: "-0.02em",
              textShadow:
                "0 0 60px var(--ic-primary-glow), 0 0 120px var(--ic-primary-glow), 0 0 200px rgba(251,191,36,0.4)",
            }}
            animate={{
              textShadow: [
                "0 0 60px var(--ic-primary-glow), 0 0 120px var(--ic-primary-glow), 0 0 200px rgba(251,191,36,0.4)",
                "0 0 80px var(--ic-primary-glow), 0 0 160px var(--ic-primary-glow), 0 0 260px rgba(251,191,36,0.55)",
                "0 0 60px var(--ic-primary-glow), 0 0 120px var(--ic-primary-glow), 0 0 200px rgba(251,191,36,0.4)",
              ],
            }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          >
            WINNER!
          </motion.h1>
        </motion.div>

        {/* Winner name — typewriter reveal */}
        {displayName && (
          <motion.div
            data-testid="projector-winner-name"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.5 }}
            className="mt-10 tracking-[0.15em]"
            style={{
              fontSize: "clamp(1.6rem, 3vw, 3.4rem)",
              color: "var(--ic-primary)",
              textShadow: "0 0 24px var(--ic-primary-glow)",
              minHeight: "clamp(2rem, 3.6vw, 4rem)",
            }}
          >
            {typed}
            {typed.length < displayName.length && (
              <motion.span
                aria-hidden
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.7, repeat: Infinity }}
                style={{ marginLeft: "0.15em" }}
              >
                |
              </motion.span>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
